import React, { useState } from 'react';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, ReferenceLine } from 'recharts';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const PredictionChart = ({ chartData, predictions }) => {
  const [timeRange, setTimeRange] = useState('7d');
  const [showPredictions, setShowPredictions] = useState(true);

  const timeRanges = [
    { value: '1d', label: '1D' },
    { value: '7d', label: '7D' },
    { value: '30d', label: '30D' },
    { value: '90d', label: '90D' },
    { value: '1y', label: '1Y' }
  ];

  const CustomTooltip = ({ active, payload, label }) => {
    if (active && payload && payload?.length) {
      return (
        <div className="bg-popover border border-border rounded-lg p-3 shadow-elevation-2">
          <p className="text-sm text-muted-foreground">{label}</p>
          <p className="text-sm font-medium text-popover-foreground">
            Price: ${payload?.[0]?.value?.toLocaleString()}
          </p>
          {payload?.[0]?.payload?.prediction && (
            <p className="text-sm text-primary">
              Prediction: ${payload?.[0]?.payload?.prediction?.toLocaleString()}
            </p>
          )}
        </div>
      );
    }
    return null;
  };

  const PredictionMarker = ({ cx, cy, payload }) => {
    if (payload?.accuracy) {
      const color = payload?.accuracy >= 90 ? '#38A169' : payload?.accuracy >= 70 ? '#D69E2E' : '#E53E3E';
      return (
        <circle 
          cx={cx} 
          cy={cy} 
          r={4} 
          fill={color} 
          stroke="white" 
          strokeWidth={2}
        />
      );
    }
    return null;
  };

  return (
    <div className="bg-card border border-border rounded-lg p-6 shadow-elevation-2">
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center space-x-3">
          <Icon name="TrendingUp" size={24} className="text-primary" />
          <h3 className="text-xl font-semibold text-card-foreground">Price Chart & Predictions</h3>
        </div>
        <div className="flex items-center space-x-2">
          <Button
            variant={showPredictions ? "default" : "outline"}
            size="sm"
            iconName="Brain"
            iconPosition="left"
            onClick={() => setShowPredictions(!showPredictions)}
          >
            AI Predictions
          </Button>
        </div>
      </div>
      <div className="flex items-center space-x-2 mb-6">
        {timeRanges?.map((range) => (
          <Button
            key={range?.value}
            variant={timeRange === range?.value ? "default" : "ghost"}
            size="sm"
            onClick={() => setTimeRange(range?.value)}
          >
            {range?.label}
          </Button>
        ))}
      </div>
      <div className="h-96 w-full">
        <ResponsiveContainer width="100%" height="100%">
          <LineChart data={chartData} margin={{ top: 5, right: 30, left: 20, bottom: 5 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="var(--color-border)" />
            <XAxis 
              dataKey="date" 
              stroke="var(--color-muted-foreground)"
              fontSize={12}
            />
            <YAxis 
              stroke="var(--color-muted-foreground)"
              fontSize={12}
              tickFormatter={(value) => `$${value?.toLocaleString()}`}
            />
            <Tooltip content={<CustomTooltip />} />
            
            <Line
              type="monotone"
              dataKey="price"
              stroke="var(--color-primary)"
              strokeWidth={2}
              dot={false}
              activeDot={{ r: 6, fill: "var(--color-primary)" }}
            />
            
            {showPredictions && (
              <Line
                type="monotone"
                dataKey="prediction"
                stroke="var(--color-secondary)"
                strokeWidth={2}
                strokeDasharray="5 5"
                dot={false}
                connectNulls={false}
              />
            )}
            
            {predictions?.map((pred, index) => (
              <ReferenceLine
                key={index}
                x={pred?.date}
                stroke="var(--color-accent)"
                strokeDasharray="2 2"
                label={{ value: `Target: $${pred?.target}`, position: "top" }}
              />
            ))}
          </LineChart>
        </ResponsiveContainer>
      </div>
      <div className="mt-4 flex items-center justify-between text-sm text-muted-foreground">
        <div className="flex items-center space-x-4">
          <div className="flex items-center space-x-2">
            <div className="w-3 h-0.5 bg-primary"></div>
            <span>Actual Price</span>
          </div>
          <div className="flex items-center space-x-2">
            <div className="w-3 h-0.5 bg-secondary border-dashed"></div>
            <span>AI Prediction</span>
          </div>
          <div className="flex items-center space-x-2">
            <div className="w-3 h-0.5 bg-accent border-dashed"></div>
            <span>Price Targets</span>
          </div>
        </div>
        <div className="flex items-center space-x-4">
          <div className="flex items-center space-x-2">
            <div className="w-2 h-2 bg-success rounded-full"></div>
            <span>High Accuracy (&gt;90%)</span>
          </div>
          <div className="flex items-center space-x-2">
            <div className="w-2 h-2 bg-warning rounded-full"></div>
            <span>Medium Accuracy (70-90%)</span>
          </div>
          <div className="flex items-center space-x-2">
            <div className="w-2 h-2 bg-error rounded-full"></div>
            <span>Low Accuracy (&lt;70%)</span>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PredictionChart;